/**
 * Datos laborales de un involucrado interno. El alta y la edicion comparten
 * este bloque para no tener el mismo formulario escrito dos veces.
 *
 * Los tres primeros (SOEID, area, puesto) ya existian; el resto son los campos
 * que el servicio de SOEID no entrega legibles —nivel, segmento y sucursal
 * llegan como codigo sin catalogo— o de plano no entrega: correo, extension y
 * jefe directo. Se capturan a mano y, donde el registro si es claro, la
 * validacion de SOEID los prellena.
 */

export const EMPTY_EMPLOYMENT = {
  additional: "", // SOEID / GEID / numero de nomina
  area: "", // area o departamento
  relationship: "", // puesto
  level: "", // nivel / C-level
  segment: "", // segmento o centro de gastos
  branch: "", // sucursal
  legal_vehicle: "", // empresa / vehiculo legal
  hire_date: "", // AAAA-MM-DD
  manager: "", // jefe directo
  corporate_email: "",
  extension: "",
};

export const EMPLOYMENT_FIELDS = Object.keys(EMPTY_EMPLOYMENT);

export const initEmployment = () => ({ ...EMPTY_EMPLOYMENT });

/** Hidrata el bloque desde un involucrado guardado. */
export const employmentFromInvolved = (involved) => {
  const values = initEmployment();
  if (!involved) return values;

  EMPLOYMENT_FIELDS.forEach((field) => {
    values[field] = involved[field] ?? "";
  });
  return values;
};

/**
 * Devuelve las llaves listas para guardar. Si el involucrado no es interno se
 * vacian todas, igual que ya se hacia con area y puesto.
 */
export const employmentToInvolved = (values, isInternal) => {
  const source = values ?? EMPTY_EMPLOYMENT;
  return EMPLOYMENT_FIELDS.reduce((acc, field) => {
    acc[field] = isInternal ? source[field] ?? "" : "";
    return acc;
  }, {});
};

/**
 * Prellena con el registro de RH lo que siga vacio. Nunca pisa lo que el
 * investigador ya escribio: el dato capturado a mano gana.
 *
 * Solo se traen los campos que el registro entrega sin ambiguedad. Puesto,
 * oficina y seccion llegan como codigo de catalogo, asi que no se copian a los
 * campos de texto —pondrian "00009740" donde deberia ir un puesto legible—.
 */
export const applySoeidToEmployment = (values, employee) => {
  if (!employee) return values;

  const fromRecord = {
    additional: employee.soeid,
    legal_vehicle: employee.company,
    hire_date: employee.hire_date,
    // El endpoint resuelve el GEID del jefe contra la nomina: si trae el
    // registro se prellena con su nombre, que es lo util en el expediente. El
    // GEID a secas solo se usa cuando ese jefe no esta en el archivo.
    manager: employee.manager?.full_name || employee.manager_geid,
  };

  const merged = { ...initEmployment(), ...values };
  Object.entries(fromRecord).forEach(([field, value]) => {
    if (value && !String(merged[field] ?? "").trim()) merged[field] = value;
  });
  return merged;
};

